import { formatWakeLoopCliCommands } from "../../runtime/cli-entry-command";

export type DaemonServiceRepairGuidance = {
  nextSteps: [string];
  summary: string;
};

export type DaemonServiceRefreshBlockedReason =
  | "foreground_service_active"
  | "lifecycle_action_in_progress";

const DAEMON_SERVICE_SETUP_COMMAND = "wakeloop setup";
const DAEMON_SERVICE_REFRESH_SUMMARY = "WakeLoop Service needs repair.";
const DAEMON_SERVICE_REFRESH_RELIABILITY_MESSAGE =
  "Reconnect WakeLoop Service before this computer can receive local Wake work.";
const DAEMON_SERVICE_UPDATE_REQUIRED_RUNNING_MESSAGE =
  "WakeLoop Service is running, but this version is not safe for new Wake work. Update WakeLoop Service.";
const DAEMON_SERVICE_UPDATE_REQUIRED_STOPPED_MESSAGE =
  "WakeLoop Service must be updated before it can start safely.";

export function formatDaemonServiceRefreshSummary(): string {
  return DAEMON_SERVICE_REFRESH_SUMMARY;
}

export function formatDaemonServiceRefreshReliabilityMessage(): string {
  return DAEMON_SERVICE_REFRESH_RELIABILITY_MESSAGE;
}

export function formatDaemonServiceUpdateRequiredMessage(input: {
  serviceIsRunning: boolean;
}): string {
  return input.serviceIsRunning
    ? DAEMON_SERVICE_UPDATE_REQUIRED_RUNNING_MESSAGE
    : DAEMON_SERVICE_UPDATE_REQUIRED_STOPPED_MESSAGE;
}

export function buildDaemonServiceUpdateRequiredNextStep(input: {
  serviceIsRunning: boolean;
}): string {
  const command = formatDaemonServiceUpdateCommand();
  return input.serviceIsRunning
    ? `Update WakeLoop Service with \`${command}\`.`
    : `Update WakeLoop Service with \`${command}\`.`;
}

export function buildDaemonServiceRefreshReadinessDetail(): string {
  return `${DAEMON_SERVICE_REFRESH_SUMMARY} This computer may still be running an older local WakeLoop Service build, so local Wake work may not be received reliably until it is repaired.`;
}

export function buildDaemonServiceRefreshPromptMessage(): string {
  return "Reconnect WakeLoop Service before this computer can receive local Wake work. WakeLoop can reconnect it for you now.";
}

export function buildDaemonServiceRefreshHandoffMessage(): string {
  return `${DAEMON_SERVICE_REFRESH_SUMMARY} Run \`${formatDaemonServiceRefreshCommand()}\` in human view, then return to WakeLoop Web.`;
}

export function buildDaemonServiceRefreshNextStep(): string {
  const repairCommand = formatDaemonServiceRefreshCommand();
  const statusCommand = formatDaemonServiceStatusCommand();
  return `${DAEMON_SERVICE_REFRESH_SUMMARY} Run \`${repairCommand}\`. If Wake or readiness still looks wrong after setup, run \`${statusCommand}\`.`;
}

export function buildDaemonServiceRefreshRetryNextStep(): string {
  return `${DAEMON_SERVICE_REFRESH_SUMMARY} Run \`${formatDaemonServiceRefreshCommand()}\`, then retry.`;
}

export function buildDaemonServiceStatusReadOnlyDetail(): string {
  return "Status is read-only. It does not change WakeLoop Service.";
}

export function buildDaemonServiceTaskRetryNextStep(): string {
  return "Try again after the current WakeLoop Service task finishes.";
}

export function buildDaemonServiceRefreshBlockedMessage(input: {
  reason: DaemonServiceRefreshBlockedReason;
}): string {
  return `${DAEMON_SERVICE_REFRESH_RELIABILITY_MESSAGE} ${buildDaemonServiceRefreshBlockedNextStep(input)}`;
}

export function buildDaemonServiceRefreshBlockedNextStep(input: {
  reason: DaemonServiceRefreshBlockedReason;
}): string {
  if (input.reason === "lifecycle_action_in_progress") {
    return buildDaemonServiceTaskRetryNextStep();
  }
  return `${formatDaemonServiceRefreshBlockedAction(input)}.`;
}

function formatDaemonServiceRefreshBlockedAction(input: {
  reason: DaemonServiceRefreshBlockedReason;
}): string {
  const unblock =
    input.reason === "foreground_service_active"
      ? "Stop the foreground service first"
      : "Wait for the current WakeLoop Service task to finish";
  return `${unblock}, then run \`${formatDaemonServiceRefreshCommand()}\``;
}

export function buildDaemonServiceRepairGuidance(): DaemonServiceRepairGuidance {
  return {
    nextSteps: [formatDaemonServiceRefreshCommand()],
    summary: buildDaemonServiceRefreshNextStep(),
  };
}

function formatDaemonServiceRefreshCommand(): string {
  const [reinstallCommand] = formatWakeLoopCliCommands([
    DAEMON_SERVICE_SETUP_COMMAND,
  ]) as [string];
  return reinstallCommand;
}

function formatDaemonServiceUpdateCommand(): string {
  const [updateCommand] = formatWakeLoopCliCommands([
    DAEMON_SERVICE_SETUP_COMMAND,
  ]) as [string];
  return updateCommand;
}

function formatDaemonServiceStatusCommand(): string {
  const [statusCommand] = formatWakeLoopCliCommands([
    "wakeloop service status",
  ]) as [string];
  return statusCommand;
}
